"use client";

import {
  API_BASE_PATH,
  AUTH_DEBUG,
  stripAppBasePath,
  withAppBasePath,
} from "@/utils/env";
import { useAuthStore, type OrganizationContext } from "@/store/use-auth-store";
import { setAuthIntent, buildReturnToPath } from "@/lib/auth-intent";
import {
  clearClientSessionArtifacts,
  clearLogoutNavigationInProgress,
  markLogoutNavigationInProgress,
} from "@/lib/auth-session";
import { useAcademicYearStore } from "@/store/use-academic-year-store";
import { useCurrentAcademicYearState } from "@/store/use-current-academic-year-state";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type ApiEnvelope<T> =
  | { success: true; data: T }
  | { success: false; error: string; meta?: unknown };

export type RequestConfig<TBody = unknown> = {
  headers?: Record<string, string>;
  body?: TBody;
  signal?: AbortSignal;
  retries?: number | null;
  query?: Record<string, string | number | boolean | undefined>;
  skipAuthRetry?: boolean;
  /** e.g. 'no-store' for hard refresh (superadmin platform). */
  cache?: RequestCache;
};

type InternalRequestConfig<TBody> = RequestConfig<TBody> & {
  authAttempt?: number;
  sessionGeneration?: number;
  sessionRevision?: string;
  keepalive?: boolean;
};

export class HttpError extends Error {
  public readonly status: number;
  public readonly data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.status = status;
    this.data = data;
    this.name = "HttpError";
  }
}

export class ForbiddenError extends HttpError {
  constructor(message = "Nedostatečná oprávnění", data?: unknown) {
    super(message, 403, data);
    this.name = "ForbiddenError";
  }
}

const extractBackendCode = (data: unknown): string | null => {
  if (!data || typeof data !== "object") return null;
  const record = data as { code?: unknown; meta?: { code?: unknown } };
  if (typeof record.code === "string" && record.code.trim().length > 0) {
    return record.code.trim();
  }
  if (typeof record.meta?.code === "string" && record.meta.code.trim().length > 0) {
    return record.meta.code.trim();
  }
  return null;
};

const isMissingCurrentAcademicYearCode = (code: string | null): boolean =>
  code === "NO_CURRENT_ACADEMIC_YEAR";

const currentPathForRecovery = (): string | null => {
  if (typeof window === "undefined") return null;
  const pathname = stripAppBasePath(window.location.pathname);
  return `${pathname}${window.location.search}${window.location.hash}`;
};

const markMissingCurrentAcademicYear = (data: unknown): void => {
  const code = extractBackendCode(data);
  if (!isMissingCurrentAcademicYearCode(code)) return;
  const orgId = useAuthStore.getState().org?.id;
  if (!orgId) return;
  useCurrentAcademicYearState.getState().markMissing(orgId, {
    errorCode: code,
    returnPath: currentPathForRecovery(),
  });
};

const clearMissingCurrentAcademicYear = (): void => {
  const orgId = useAuthStore.getState().org?.id;
  if (!orgId) return;
  useCurrentAcademicYearState.getState().markAvailable(orgId);
};

const extractErrorMessage = (data: unknown, fallback: string): string => {
  const normalizeMessageValue = (value: unknown): string | null => {
    if (typeof value === "string" && value.length > 0) {
      return value;
    }
    if (Array.isArray(value)) {
      const messages = value.filter((item): item is string => typeof item === "string" && item.length > 0);
      if (messages.length > 0) {
        return messages.join("\n");
      }
    }
    return null;
  };

  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    const message = normalizeMessageValue(record.message);
    if (message) {
      return message;
    }
    const error = normalizeMessageValue(record.error);
    if (error) {
      return error;
    }
  }
  return fallback;
};

const LOGIN_REDIRECT = "/login?reason=expired";
const PUBLIC_ROUTES = ["/login", "/register", "/forgot-password", "/reset-password"];
const isPublicRoutePath = (path: string) =>
  PUBLIC_ROUTES.includes(path) || path.startsWith("/reset-password/");

const DEFAULT_HEADERS: Record<string, string> = {
  "Content-Type": "application/json",
};

type PersistedAuthState = {
  org?: OrganizationContext | null;
};

const readPersistedAuthState = (): PersistedAuthState | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem("skillstorm_auth");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: PersistedAuthState };
    return parsed.state ?? null;
  } catch {
    return null;
  }
};

const readCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  return decodeURIComponent(match.split("=").slice(1).join("="));
};

export const getCsrfHeaders = (): Record<string, string> => {
  const csrf = readCookie("ss_csrf");
  return csrf ? { "x-csrf-token": csrf } : {};
};

export const createCorrelationId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `cid-${Date.now()}-${Math.floor(Math.random() * 10_000)}`;
};

const resolveUrl = (path: string): string => {
  if (/^https?:\/\//i.test(path)) return path;
  const sanitizedPath = path.startsWith("/") ? path : `/${path}`;
  if (sanitizedPath === API_BASE_PATH || sanitizedPath.startsWith(`${API_BASE_PATH}/`) || sanitizedPath.startsWith(`${API_BASE_PATH}?`)) return sanitizedPath;
  if (sanitizedPath === "/api" || sanitizedPath.startsWith("/api/")) {
    return withAppBasePath(sanitizedPath);
  }
  return `${API_BASE_PATH}${sanitizedPath}`;
};

const applyQuery = (
  path: string,
  query?: Record<string, string | number | boolean | undefined>,
): string => {
  if (!query || !Object.keys(query).length) return path;

  const isAbsolute = /^https?:\/\//i.test(path);
  const base = isAbsolute
    ? path
    : `https://dummy.base${path.startsWith("/") ? "" : "/"}${path}`;

  const url = new URL(base);
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined) return;
    url.searchParams.set(key, String(value));
  });

  if (isAbsolute) return url.toString();
  return `${url.pathname}${url.search}`;
};

const waitWithSignal = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (ms <= 0) {
      resolve();
      return;
    }

    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);

    const onAbort = () => {
      cleanup();
      const reason =
        (signal?.reason as Error | undefined)?.message ?? "Aborted by signal";
      reject(new Error(reason));
    };

    const cleanup = () => {
      if (signal) signal.removeEventListener("abort", onAbort);
      clearTimeout(timer);
    };

    if (signal) {
      signal.addEventListener("abort", onAbort, { once: true });
      if (signal.aborted) onAbort();
    }
  });

class RateLimiter {
  private active = 0;

  constructor(
    private readonly maxConcurrent = 5,
    private readonly baseDelay = 40,
  ) { }

  async acquire(signal?: AbortSignal): Promise<void> {
    let attempt = 0;
    while (this.active >= this.maxConcurrent) {
      const backoff = Math.min(500, this.baseDelay * 2 ** attempt);
      await waitWithSignal(backoff, signal);
      attempt += 1;
    }
    this.active += 1;
  }

  release(): void {
    this.active = Math.max(0, this.active - 1);
  }
}

const limiter = new RateLimiter();
let refreshPromise: Promise<void> | null = null;
let csrfBootstrapPromise: Promise<void> | null = null;


// Cookie-writing auth operations and refresh share the same queue/Browser lock.
// The lock covers response headers, when the browser applies Set-Cookie.
let sessionCookieQueue: Promise<unknown> = Promise.resolve();
let sessionGeneration = 0;

export class SessionChangedError extends Error {
  constructor() { super("Přihlášení se mezitím změnilo. Otevři aktivitu znovu."); this.name = "SessionChangedError"; }
}

export function invalidateHttpSessionRequests(): void { sessionGeneration += 1; }

export function readSharedSessionRevision(): string {
  // This readable nonce only cancels stale work; it never grants access.
  const cookie = readCookie("ss_session_epoch");
  if (cookie) return cookie;
  try { return JSON.parse(window.localStorage.getItem("skillstorm_session_change") ?? "null")?.id ?? ""; }
  catch { return ""; }
}

export function publishSessionChange(kind: "changed" | "signed-out"): void {
  if (typeof window === "undefined") return;
  const id = createCorrelationId();
  document.cookie = "ss_session_epoch=" + encodeURIComponent(id) + "; Path=/; SameSite=Lax" + (window.location.protocol === "https:" ? "; Secure" : "");
  try { window.localStorage.setItem("skillstorm_session_change", JSON.stringify({ kind, id })); }
  catch { /* The shared cookie nonce still blocks stale request replay. */ }
}

function requireSessionGeneration(expected: number, revision?: string): void {
  if (expected !== sessionGeneration || (revision !== undefined && revision !== readSharedSessionRevision())) throw new SessionChangedError();
}

async function withSessionCookieLock<T>(operation: () => Promise<T>): Promise<T> {
  let cancelled = false;
  let acquired = false;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<never>((_, reject) => {
    timeout = setTimeout(() => {
      if (acquired) return;
      cancelled = true;
      reject(new HttpError("Ověření přihlášení stále probíhá. Zkus akci za chvíli znovu.", 503, null));
    }, 10_000);
  });
  const locked = async () => {
    if (cancelled) throw new SessionChangedError();
    acquired = true;
    if (timeout) clearTimeout(timeout);
    return operation();
  };
  const queued = sessionCookieQueue.catch(() => undefined).then(async () => {
    if (cancelled) throw new SessionChangedError();
    return await (typeof navigator !== "undefined" && navigator.locks
      ? navigator.locks.request("skillstorm-refresh:" + API_BASE_PATH, locked)
      : locked());
  });
  sessionCookieQueue = queued.then(() => undefined, () => undefined);
  try { return await Promise.race([queued, timedOut]); }
  finally { if (timeout) clearTimeout(timeout); }
}

const COOKIE_AUTH_ACTIONS = new Set([
  "login", "register", "sso/google", "logout", "use-org",
  "switch-organization", "switch-role", "change-password", "reset-password",
]);
const RENEWABLE_CONTEXT_ACTIONS = new Set(["use-org", "switch-organization", "switch-role", "change-password"]);
function cookieAuthAction(path: string): string | null {
  const normalized = normalizePath(path).replace(/\/+$/, "");
  const marker = normalized.lastIndexOf("/auth/");
  if (marker < 0) return null;
  const action = normalized.slice(marker + "/auth/".length);
  return COOKIE_AUTH_ACTIONS.has(action) ? action : null;
}

const normalizePath = (path: string): string => {
  if (/^https?:\/\//i.test(path)) {
    try {
      const url = new URL(path);
      return url.pathname ?? path;
    } catch {
      return path;
    }
  }
  return path.startsWith("/") ? path : `/${path}`;
};

const isOnPublicRoute = (): boolean => {
  if (typeof window === "undefined") return false;
  return isPublicRoutePath(stripAppBasePath(window.location.pathname));
};

const shouldAllowProfileRetry = (path: string): boolean => {
  const normalized = normalizePath(path).replace(/\/+$/, "");
  return normalized.endsWith("/me");
};

const shouldBypassAuthRetry = (path: string): boolean => {
  const normalized = normalizePath(path).replace(/\/+$/, "");
  return (
    normalized === "/auth/login" ||
    normalized === "/auth/register" ||
    normalized === "/auth/logout" ||
    normalized === "/auth/refresh"
  );
};

const logoutAndRedirect = (): void => {
  invalidateHttpSessionRequests();
  publishSessionChange("signed-out");
  // Clear local state first (avoid UI thinking it is still authed)
  markLogoutNavigationInProgress();
  useAuthStore.getState().clearAuthState();
  useAcademicYearStore.getState().clearAll();
  useCurrentAcademicYearState.getState().clearAll();
  clearClientSessionArtifacts();

  if (typeof window !== "undefined") {
    const currentPath = stripAppBasePath(window.location.pathname);
    // ⛔ Do not store RETURN_TO or redirect if already on login/register (prevent loop)
    if (currentPath === "/login" || currentPath === "/register") {
      return;
    }

    // Preserve current path so PostAuthResolver can return user after re-login (fixes 401 "black hole")
    const path = buildReturnToPath();
    setAuthIntent({ type: "RETURN_TO", path });

    window.location.replace(withAppBasePath(LOGIN_REDIRECT));
    clearLogoutNavigationInProgress();
  }
};

const buildHeaders = (configHeaders?: Record<string, string>): Record<string, string> => {
  const state = useAuthStore.getState();
  const persisted = !state.org ? readPersistedAuthState() : null;

  const headers: Record<string, string> = {
    ...DEFAULT_HEADERS,
    ...configHeaders,
    "x-cid": createCorrelationId(),
  };

  const orgId =
    state.org?.id ?? persisted?.org?.id ?? state.user?.organizationId ?? null;
  if (orgId) {
    headers["x-org-id"] = orgId;
  }

  if (typeof document !== "undefined") {
    const csrf = readCookie("ss_csrf");
    if (csrf) {
      headers["x-csrf-token"] = csrf;
    }
  }

  return headers;
};

const performFetch = async (
  method: HttpMethod,
  path: string,
  config: InternalRequestConfig<unknown> = {},
): Promise<Response> => {
  // Do this before acquiring a mutation slot: the bootstrap GET must be able
  // to run even when several first-time requests arrive simultaneously.
  if (method !== "GET" && typeof document !== "undefined") {
    await ensureCsrfToken();
  }
  const controller = new AbortController();
  if (config.signal) {
    config.signal.addEventListener(
      "abort",
      () => controller.abort(config.signal?.reason),
      { once: true },
    );
  }

  await limiter.acquire(controller.signal);

  const cache =
    config.cache !== undefined
      ? config.cache
      : method === "GET"
        ? "no-cache"
        : undefined;
  const headers = buildHeaders(config.headers);
  const isFormData =
    typeof FormData !== "undefined" && config.body instanceof FormData;
  if (isFormData) {
    // The browser must generate multipart/form-data with the boundary. Keeping
    // our JSON default here would make Multer see an invalid request body.
    delete headers["Content-Type"];
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: "include",
    mode: "cors",
    signal: controller.signal,
    ...(config.keepalive ? { keepalive: true } : {}),
    ...(cache !== undefined && { cache }),
  };

  if (config.body !== undefined) {
    if (
      typeof config.body === "string" ||
      config.body instanceof FormData
    ) {
      init.body = config.body;
    } else {
      init.body = JSON.stringify(config.body);
    }
  }

  const url = resolveUrl(applyQuery(path, config.query));

  try {
    // CSRF bootstrap and the concurrency limiter may have yielded while another
    // tab changed identity. Never dispatch an old operation with new cookies.
    if (config.sessionGeneration !== undefined) {
      requireSessionGeneration(config.sessionGeneration, config.sessionRevision);
    }
    return await fetch(url, init);
  } finally {
    limiter.release();
  }
};

const parseResponse = async <T>(response: Response): Promise<T> => {
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  if (!text) return undefined as T;

  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
};

async function renewSessionCookies(): Promise<void> {
  const response = await performFetch("POST", "/auth/refresh", { authAttempt: 1 });
  if (!response.ok) {
    const data = await parseResponse<unknown>(response).catch(() => null);
    throw new HttpError("Refresh failed", response.status, data);
  }
  await parseResponse<unknown>(response).catch(() => null);
}

export const refreshSession = async (): Promise<void> => {
  if (refreshPromise) return refreshPromise;
  const expected = sessionGeneration;
  const revision = readSharedSessionRevision();
  const csrfBefore = readCookie("ss_csrf");
  const pending = withSessionCookieLock(async () => {
    requireSessionGeneration(expected, revision);
    // A sibling tab may have completed renewal while this one waited.
    if (csrfBefore && readCookie("ss_csrf") !== csrfBefore && readCookie("ss_csrf")) return;
    await renewSessionCookies();
    requireSessionGeneration(expected, revision);
  });
  refreshPromise = pending;
  try { await pending; }
  finally { if (refreshPromise === pending) refreshPromise = null; }
};

async function performSessionMutation(
  method: HttpMethod,
  path: string,
  action: string,
  config: InternalRequestConfig<unknown>,
): Promise<Response> {
  invalidateHttpSessionRequests();
  const expected = sessionGeneration;
  const revision = readSharedSessionRevision();
  try {
    return await withSessionCookieLock(async () => {
      requireSessionGeneration(expected, revision);
      let response = await performFetch(method, path, config);
      requireSessionGeneration(expected, revision);
      // The user may switch context after 15 idle minutes. Renew within the
      // already held lock; calling refreshSession here would deadlock.
      if (response.status === 401 && RENEWABLE_CONTEXT_ACTIONS.has(action)) {
        await renewSessionCookies();
        requireSessionGeneration(expected, revision);
        response = await performFetch(method, path, config);
        requireSessionGeneration(expected, revision);
      }
      // Publish the revision while still holding the cookie lock. A sibling
      // request can synchronously reject its old identity before replaying.
      if (response.ok) publishSessionChange(action === "logout" ? "signed-out" : "changed");
      return response;
    });
  } finally {
    // Requests begun while the identity operation was pending cannot later
    // retry against its replacement cookies, even if that operation failed.
    if (expected === sessionGeneration) invalidateHttpSessionRequests();
  }
}

const handleUnauthorized = async <TResponse, TBody>(
  method: HttpMethod,
  path: string,
  config: InternalRequestConfig<TBody>,
): Promise<TResponse> => {
  requireSessionGeneration(config.sessionGeneration ?? sessionGeneration, config.sessionRevision);
  if (isOnPublicRoute()) {
    throw new HttpError("Unauthorized", 401, null);
  }
  const attempt = config.authAttempt ?? 0;
  const maxAttempts = shouldAllowProfileRetry(path) ? 2 : 1;

  if (attempt >= maxAttempts) {
    logoutAndRedirect();
    throw new HttpError("Session expired", 401, null);
  }

  try {
    await refreshSession();
  } catch (error) {
    // A network outage, rate limit or server failure does not revoke a session.
    // Let the caller keep its activity and retry with the existing cookies.
    if (!(error instanceof HttpError) || (error.status !== 401 && error.status !== 403)) {
      throw error;
    }
    logoutAndRedirect();
    throw new HttpError("Session expired", 401, null);
  }

  requireSessionGeneration(config.sessionGeneration ?? sessionGeneration, config.sessionRevision);
  return request<TResponse, TBody>(method, path, {
    ...config,
    authAttempt: attempt + 1,
  });
};

export const request = async <TResponse = unknown, TBody = unknown>(
  method: HttpMethod,
  path: string,
  config: InternalRequestConfig<TBody> = {},
): Promise<TResponse> => {
  const expected = config.sessionGeneration ?? sessionGeneration;
  const revision = config.sessionRevision ?? readSharedSessionRevision();
  const action = method === "POST" ? cookieAuthAction(path) : null;
  const response = action
    ? await performSessionMutation(method, path, action, config)
    : await performFetch(method, path, { ...config, sessionGeneration: expected, sessionRevision: revision });
  if (!action) requireSessionGeneration(expected, revision);

  if (AUTH_DEBUG) {
    const normalized = normalizePath(path);
    if (normalized.startsWith("/auth/login") || normalized.startsWith("/auth/me")) {
      console.log(
        "%c[AUTH][HTTP]",
        "color:#16a34a;font-weight:600",
        normalized,
        response.status,
      );
    }
  }

  if (response.status === 401) {
    if (config.skipAuthRetry) {
      const data = await parseResponse<unknown>(response);
      throw new HttpError(
        extractErrorMessage(data, "Unauthorized"),
        response.status,
        data,
      );
    }
    if (shouldBypassAuthRetry(path)) {
      const data = await parseResponse<unknown>(response);
      throw new HttpError(
        extractErrorMessage(data, "Unauthorized"),
        response.status,
        data,
      );
    }
    return handleUnauthorized<TResponse, TBody>(method, path, { ...config, sessionGeneration: expected, sessionRevision: revision });
  }

  if (response.status === 403) {
    const data = await parseResponse<unknown>(response);
    markMissingCurrentAcademicYear(data);
    throw new ForbiddenError(
      extractErrorMessage(data, "Nedostatečná oprávnění"),
      data,
    );
  }

  if (!response.ok) {
    const data = await parseResponse<unknown>(response);
    markMissingCurrentAcademicYear(data);
    throw new HttpError(
      extractErrorMessage(data, "HTTP error"),
      response.status,
      data,
    );
  }

  // Your API typically responds with ApiEnvelope<T>.
  // But some endpoints may return raw T; support both.
  const parsed = await parseResponse<ApiEnvelope<TResponse> | TResponse>(response);
  if (!action) requireSessionGeneration(expected, revision);

  if (parsed && typeof parsed === "object" && "success" in (parsed as Record<string, unknown>)) {
    const envelope = parsed as ApiEnvelope<TResponse>;
    if (envelope.success === false) {
      throw new HttpError(envelope.error ?? "API error", response.status, envelope.meta);
    }
    if (
      path.startsWith("/academic-years/current") ||
      (method === "POST" && path.startsWith("/academic-years")) ||
      (method === "PATCH" && /\/academic-years\/[^/]+\/activate$/.test(path))
    ) {
      clearMissingCurrentAcademicYear();
    }
    return (envelope as { success: true; data: TResponse }).data;
  }

  if (
    path.startsWith("/academic-years/current") ||
    (method === "POST" && path.startsWith("/academic-years")) ||
    (method === "PATCH" && /\/academic-years\/[^/]+\/activate$/.test(path))
  ) {
    clearMissingCurrentAcademicYear();
  }

  return parsed as TResponse;
};

const withBody = <TBody>(
  config: RequestConfig<TBody> | undefined,
  body: TBody | undefined,
): InternalRequestConfig<TBody> => {
  if (body === undefined) {
    return { ...(config ?? {}) };
  }
  return { ...(config ?? {}), body };
};

export const httpClient = {
  request,

  get<TResponse>(
    path: string,
    config?: RequestConfig,
  ): Promise<TResponse> {
    return request<TResponse>("GET", path, config);
  },

  post<TResponse, TBody = unknown>(
    path: string,
    body?: TBody,
    config?: RequestConfig<TBody>,
  ): Promise<TResponse> {
    return request<TResponse, TBody>(
      "POST",
      path,
      withBody(config, body),
    );
  },

  put<TResponse, TBody = unknown>(
    path: string,
    body?: TBody,
    config?: RequestConfig<TBody>,
  ): Promise<TResponse> {
    return request<TResponse, TBody>(
      "PUT",
      path,
      withBody(config, body),
    );
  },

  patch<TResponse, TBody = unknown>(
    path: string,
    body?: TBody,
    config?: RequestConfig<TBody>,
  ): Promise<TResponse> {
    return request<TResponse, TBody>(
      "PATCH",
      path,
      withBody(config, body),
    );
  },

  delete<TResponse>(
    path: string,
    config?: RequestConfig,
  ): Promise<TResponse> {
    return request<TResponse>("DELETE", path, config);
  },
};

export async function ensureCsrfToken(): Promise<void> {
  if (readCookie("ss_csrf")) return;

  if (!csrfBootstrapPromise) {
    const bootstrap = async () => {
      // Another tab may have bootstrapped while this one waited for its lock.
      if (readCookie("ss_csrf")) return;
      await request<{ csrfToken: string }>("GET", "/auth/csrf", {
        skipAuthRetry: true,
        cache: "no-store",
      });

      if (!readCookie("ss_csrf")) {
        throw new HttpError("CSRF token bootstrap failed", 500, null);
      }
    };
    // A single bootstrap prevents competing Set-Cookie responses from giving
    // concurrent mutations different double-submit tokens.
    csrfBootstrapPromise = typeof navigator !== "undefined" && navigator.locks
      ? navigator.locks.request(`skillstorm-csrf:${API_BASE_PATH}`, bootstrap).then(() => undefined)
      : bootstrap();
  }
  const pending = csrfBootstrapPromise;
  try {
    await pending;
  } finally {
    if (csrfBootstrapPromise === pending) csrfBootstrapPromise = null;
  }
}

export async function logoutSession(): Promise<void> {
  await request("POST", "/auth/logout", {
    skipAuthRetry: true,
    cache: "no-store",
    keepalive: true,
  });
}

export async function fetchWithAuth<T>(
  method: string,
  url: string,
  options?: {
    body?: unknown;
    signal?: AbortSignal;
  } & RequestConfig<unknown>,
): Promise<T> {
  return request<T, unknown>(method as HttpMethod, url, options);
}
